/**
 * What a home page keeps outside the feature page (`../proof/`): the search snippet, the questions and their
 * FAQPage data, and the WebApplication data. The page's own words are `../proof/copy.json`.
 */
import type {Lang} from '@product/quies/lib/hreflang'
import {CATALOGUE} from '@product/quies/i18n/catalogue'
import copy from '@product/quies/proof/copy.json'

export type HomeFaqItem = {question: string; answer: string}

export type HomeStructuredData = {
    /** Distinct from `meta.description` — the WebApplication schema carries its own copy. */
    description: string
    inLanguage?: string
    offerDescription: string
    featureList: string[]
    keywords: string
}

export type HomeContent = {
    lang: Lang
    meta: {title: string; description: string; ogTitle: string; ogDescription: string}
    faq: {heading: string; items: HomeFaqItem[]}
    structuredData: HomeStructuredData
}

export const HOME_CONTENT: Record<Lang, HomeContent> = {
    en: {
        lang: 'en',
        meta: {
            title: 'AI Hypnosis | Self-Hypnosis Written for You, Starting Now',
            description:
                'AI hypnosis that starts now: Quies writes an Ericksonian self-hypnosis session from your emotional state, unique each time. Your first session is free. You will sign in before you listen.',
            ogTitle: 'AI Hypnosis | Self-Hypnosis Written for You | Quies.ai',
            ogDescription:
                'AI hypnosis that starts now: Quies writes an Ericksonian self-hypnosis session from your emotional state, unique each time. Your first session is free. You will sign in before you listen.',
        },
        faq: {
            heading: 'Questions & Answers',
            items: [
                {
                    question: 'What is Quies?',
                    answer: 'Quies is self-hypnosis guided by AI, built on Ericksonian methodology — the clinical tradition behind modern hypnotherapy. Before each session, the AI assesses your emotional state. Then it writes a hypnotic session for precisely what you carry in that moment. Not selected from a library. Generated.',
                },
                {
                    question: 'What is Ericksonian self-hypnosis?',
                    answer: 'Developed by psychiatrist Milton H. Erickson, Ericksonian hypnosis works through indirect suggestion, metaphor, and naturalistic trance — meeting the mind where it already is rather than commanding it. It is the methodology most used by clinical hypnotherapists. Quies applies these principles through AI, making them available without scheduling a $120 session.',
                },
                {
                    question: 'How does a session work?',
                    answer: 'You start with a brief interactive assessment: emotional sliders, a mood grid, optionally a few words about what is on your mind. The AI processes this context and writes a full hypnotic session — narrative, cadence, metaphor, depth cues — tailored to your state. Sessions run 15 to 20 minutes of guided audio.',
                },
                {
                    question: 'Is this the same as meditation?',
                    answer: 'No. Meditation trains attention and awareness. Self-hypnosis induces a focused trance state to access deeper layers of cognition and feeling. The mechanisms differ, the experience differs, and so does what becomes possible. Some people use both for different purposes.',
                },
                {
                    question: 'Do I need prior experience?',
                    answer: 'None. The session is written to meet you where you are. If it is your first time entering hypnotic trance, the language guides you there. The AI adapts to what you share, not to an assumed baseline.',
                },
            ],
        },
        structuredData: {
            description:
                'AI hypnosis: Ericksonian self-hypnosis sessions, each written from your emotional state rather than selected from a library. It starts in the browser, with no download.',
            offerDescription: 'Your first session is free. You will sign in before you listen.',
            featureList: [
                'Personalized Ericksonian self-hypnosis sessions',
                'AI emotional state assessment',
                '15-20 minute guided audio sessions',
                'Generated in real time, unique each session',
                'No download required',
            ],
            keywords: 'AI hypnosis, self-hypnosis, AI self-hypnosis, Ericksonian hypnosis, personalized hypnotherapy, self-hypnosis app',
        },
    },

    fr: {
        lang: 'fr',
        meta: {
            title: 'Hypnose Ericksonienne par IA | Séances Personnalisées',
            description:
                "Quies génère des séances d'hypnose ericksonienne personnalisées à partir de votre état émotionnel. Héritage de Nancy, augmenté par l'IA.",
            ogTitle: 'Hypnose Ericksonienne par IA | Séances Personnalisées | Quies.ai',
            ogDescription:
                "Quies génère des séances d'hypnose ericksonienne personnalisées à partir de votre état émotionnel. Héritage de Nancy, augmenté par l'IA.",
        },
        faq: {
            heading: 'Questions fréquentes',
            items: [
                {
                    question: "Quel est le lien entre Quies et l'hypnose ericksonienne ?",
                    answer: "Quies est construit sur la méthodologie ericksonienne -- la tradition clinique qui a transformé l'hypnothérapie moderne. Avant chaque séance, l'IA évalue votre état émotionnel. Puis elle écrit une séance hypnotique complète -- narration, métaphores, rythme, profondeur -- adaptée à ce que vous portez à cet instant. Rien n'est sélectionné dans une bibliothèque. Tout est généré.",
                },
                {
                    question: 'Comment se déroule une séance ?',
                    answer: "Vous commencez par une brève évaluation interactive : curseurs émotionnels, grille d'humeur, et optionnellement quelques mots sur ce qui vous occupe. L'IA traite ce contexte et écrit une séance hypnotique de 15 à 20 minutes -- induction, approfondissement, narration, métaphores, retour. Chaque séance est unique.",
                },
                {
                    question: 'Quelle différence avec la sophrologie ?',
                    answer: "La sophrologie, créée par Alfonso Caycedo en 1960, combine relaxation progressive, respiration et visualisation positive. L'hypnose ericksonienne travaille différemment : elle utilise la suggestion indirecte et la métaphore pour accéder à des couches plus profondes de la cognition, sans passer par la volonté consciente. Les deux approches partagent des racines dans l'histoire française de la psychothérapie, mais les mécanismes et la profondeur d'intervention diffèrent.",
                },
                {
                    question: 'Est-ce la même chose que la méditation ?',
                    answer: "Non. La méditation entraîne l'attention et la conscience. L'hypnose induit un état de trance focalisé pour accéder à des couches plus profondes de cognition et de ressenti. Les mécanismes, l'expérience et les résultats diffèrent. Certaines personnes utilisent les deux pour des objectifs différents.",
                },
                {
                    question: 'Faut-il une expérience préalable ?',
                    answer: "Aucune. La séance est écrite pour vous rencontrer là où vous êtes. Si c'est votre première transe hypnotique, le langage vous y conduit naturellement. L'IA s'adapte à ce que vous partagez, pas à un niveau de référence supposé. Si vous avez déjà pratiqué la sophrologie ou la relaxation, vous reconnaîtrez certaines structures, mais à un autre niveau de profondeur.",
                },
            ],
        },
        structuredData: {
            description:
                "Quies génère des séances d'hypnose ericksonienne personnalisées à partir de votre état émotionnel. L'héritage de l'école de Nancy, augmenté par l'intelligence artificielle.",
            inLanguage: 'fr',
            offerDescription: 'Votre première séance est offerte. Vous vous connecterez avant de l’écouter.',
            featureList: [
                "Séances d'hypnose ericksonienne personnalisées par IA",
                "Évaluation de l'état émotionnel avant chaque séance",
                "15-20 minutes d'audio guidé",
                'Générées en temps réel, uniques à chaque fois',
                'Aucun téléchargement requis',
            ],
            keywords: 'hypnose ericksonienne app, sophrologie ia, application hypnose, auto-hypnose app',
        },
    },

    de: {
        lang: 'de',
        meta: {
            title: 'KI-Hypnose | Selbsthypnose, für Sie geschrieben | Quies',
            description:
                'KI-Hypnose, die jetzt beginnt: Quies schreibt eine ericksonsche Hypnosesitzung aus Ihrem aktuellen Zustand, jedes Mal neu. Ihre erste Sitzung ist kostenlos. Sie melden sich an, bevor Sie zuhören.',
            ogTitle: 'KI-Hypnose | Selbsthypnose, für Sie geschrieben | Quies',
            ogDescription:
                'KI-Hypnose, die jetzt beginnt: Quies schreibt eine ericksonsche Hypnosesitzung aus Ihrem aktuellen Zustand, jedes Mal neu. Ihre erste Sitzung ist kostenlos. Sie melden sich an, bevor Sie zuhören.',
        },
        faq: {
            heading: 'Häufige Fragen',
            items: [
                {
                    question: 'Was ist KI-Hypnose?',
                    answer: 'Bei Quies schreibt eine KI eine Hypnosesitzung nach der Methode Milton Ericksons -- nicht aus einer Bibliothek ausgewählt, sondern für den Zustand, den Sie vorher beschrieben haben. Die Sitzung arbeitet mit indirekter Suggestion, Bildern und Metaphern und wird Ihnen vorgesprochen. Sie beginnt im Browser, ohne Download und ohne Anmeldung.',
                },
                {
                    question: 'Wie funktioniert eine Sitzung?',
                    answer: 'Sie beginnen mit einer kurzen Einschätzung: emotionale Regler, ein Stimmungsraster, optional einige Worte über das, was Sie gerade beschäftigt. Die KI analysiert diesen Kontext und schreibt eine vollständige hypnotische Sitzung -- Erzählung, Metaphern, Rhythmus, Tiefenhinweise -- abgestimmt auf genau diesen Moment. Jede Sitzung dauert 15 bis 20 Minuten.',
                },
                {
                    question: 'Ist das wie eine Meditations-App?',
                    answer: 'Nein. Meditation trainiert Aufmerksamkeit und Bewusstsein. Selbsthypnose induziert einen fokussierten Trancezustand, um tiefere kognitive und emotionale Schichten zu erreichen. Die Mechanismen, die Erfahrung und die Ergebnisse unterscheiden sich grundlegend. Manche Menschen nutzen beides für verschiedene Zwecke.',
                },
                {
                    question: 'Brauche ich Vorerfahrung mit Hypnose?',
                    answer: 'Keine. Die Sitzung wird für Ihren aktuellen Zustand geschrieben, nicht für ein angenommenes Erfahrungsniveau. Wenn Sie noch nie in hypnotischer Trance waren, führt die Sprache Sie dorthin. Wenn Sie Erfahrung mit AT oder Progressiver Muskelentspannung haben, werden Sie die tiefere Struktur wiedererkennen.',
                },
                {
                    question: 'Wird Quies von der Krankenkasse übernommen?',
                    answer: 'Nein. Quies ist ein Wellness-Tool zur Entspannung und Selbsterforschung, kein Medizinprodukt. Anders als klassisches Autogenes Training, das manche Kassen als Präventionskurs erstatten, ist Quies ein digitaler Dienst ohne Kassenzulassung. Wenn Sie eine psychische Krise erleben, wenden Sie sich bitte an eine Fachperson.',
                },
            ],
        },
        structuredData: {
            description:
                'KI-Hypnose: ericksonsche Selbsthypnose, jede Sitzung aus Ihrem aktuellen Zustand geschrieben statt aus einer Bibliothek gewählt. Sie beginnt im Browser, ohne Download.',
            inLanguage: 'de',
            offerDescription: 'Ihre erste Sitzung ist kostenlos. Sie melden sich an, bevor Sie zuhören.',
            featureList: [
                'KI-personalisierte ericksonsche Selbsthypnose',
                'Emotionale Zustandserfassung vor jeder Sitzung',
                '15-20 Minuten geführte Audio-Sitzungen',
                'In Echtzeit generiert, jedes Mal einzigartig',
                'Kein Download erforderlich',
            ],
            keywords: 'ki hypnose, hypnose app, selbsthypnose, ericksonsche hypnose app',
        },
    },

    es: {
        lang: 'es',
        meta: {
            title: 'Hipnosis para Dormir y Relajación con IA | Quies',
            description:
                'Quies genera sesiones de hipnosis ericksoniana personalizadas desde tu estado emocional. Descanso consciente potenciado por inteligencia artificial.',
            ogTitle: 'Hipnosis para Dormir y Relajación con IA | Quies',
            ogDescription:
                'Quies genera sesiones de hipnosis ericksoniana personalizadas desde tu estado emocional. Descanso consciente potenciado por inteligencia artificial.',
        },
        faq: {
            heading: 'Preguntas frecuentes',
            items: [
                {
                    question: '¿Qué diferencia a Quies de otras apps de relajación?',
                    answer: 'La mayoría de apps reproducen grabaciones idénticas para todos. Quies no selecciona de una biblioteca: antes de cada sesión, la IA evalúa tu estado emocional y escribe una sesión hipnótica completa -- narrativa, metáforas, ritmo, profundidad -- adaptada a lo que sientes en ese momento. Cada sesión es única.',
                },
                {
                    question: '¿Qué es la hipnosis ericksoniana?',
                    answer: 'Desarrollada por el psiquiatra Milton H. Erickson, trabaja mediante sugerencia indirecta, metáfora y trance naturalista. En lugar de dar órdenes al subconsciente, lo encuentra donde ya está. Es la metodología más utilizada por hipnoterapeutas clínicos en el mundo. Quies aplica estos principios a través de inteligencia artificial.',
                },
                {
                    question: '¿Cómo funciona una sesión?',
                    answer: 'Comienzas con una breve evaluación interactiva: controles emocionales, una cuadrícula de estado de ánimo, y opcionalmente unas palabras sobre lo que te ocupa. La IA analiza ese contexto y genera una sesión hipnótica completa de 15 a 20 minutos, con narrativa, cadencia y metáforas escritas para ese preciso momento.',
                },
                {
                    question: '¿Es lo mismo que la meditación?',
                    answer: 'No. La meditación entrena la atención y la conciencia. La autohipnosis induce un estado de trance focalizado para acceder a capas más profundas de la mente. Los mecanismos, la experiencia y los resultados son diferentes. Algunas personas usan ambas para propósitos distintos.',
                },
                {
                    question: '¿Necesito experiencia previa con hipnosis?',
                    answer: 'Ninguna. La sesión está escrita para encontrarte donde estás. Si es tu primera experiencia con trance hipnótico, el lenguaje te guía de forma natural. La IA se adapta a lo que compartes, no a un nivel de experiencia asumido.',
                },
            ],
        },
        structuredData: {
            description:
                'Quies genera sesiones de hipnosis ericksoniana personalizadas desde tu estado emocional. Descanso consciente potenciado por inteligencia artificial.',
            inLanguage: 'es',
            offerDescription: 'Tu primera sesión es gratis. Iniciarás sesión antes de escucharla.',
            featureList: [
                'Sesiones de hipnosis ericksoniana personalizadas por IA',
                'Evaluación del estado emocional antes de cada sesión',
                '15-20 minutos de audio guiado',
                'Generadas en tiempo real, únicas cada vez',
                'Sin descarga necesaria',
            ],
            keywords: 'hipnosis para dormir app, relajación guiada ia, hipnosis ericksoniana, app hipnosis',
        },
    },

    ja: {
        lang: 'ja',
        meta: copy.ja.meta,
        faq: copy.ja.faq,
        structuredData: {...copy.ja.structured, offerDescription: CATALOGUE.ja.session.firstSessionNote, inLanguage: 'ja'},
    },

    zh: {
        lang: 'zh',
        meta: copy.zh.meta,
        faq: copy.zh.faq,
        structuredData: {...copy.zh.structured, offerDescription: CATALOGUE.zh.session.firstSessionNote, inLanguage: 'zh-CN'},
    },

}
